import { useState } from "react";
import { Row, Col } from "react-bootstrap";
import Collapse from "react-bootstrap/Collapse";
import { CaretRightFill, CaretDownFill } from "react-bootstrap-icons";

import { MeterFieldProps } from "../../../types/salesDocuments.types";
import { useMeterContext } from "../meterContext";

export const ExtraDetailsComponent: React.FC<MeterFieldProps> = ({ meter, readOnly, hidden }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const ctx = useMeterContext();

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  if (hidden) return null;

  const apiFields = ctx.apiDataLoaded && (
    <>
      <hr className="sales-doc-divider" />
      <div className="card border-0">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            toggleOpen();
          }}
        >
          Extra Details {isOpen ? <CaretDownFill /> : <CaretRightFill />}{" "}
        </button>
      </div>
      <Collapse in={isOpen}>
        <div className="container mt-5">
          <Row>
            <Col>
              {(ctx.mpanTopLine || ctx.mpanBottomLine) && (
                <div className="sales-doc-field mt-2 read-only-field">
                  <span
                    id={`mpan-meter-label-${meter.id.toString()}`}
                    className="sales-doc-group-label"
                  >
                    MPAN
                  </span>
                  <div role="group" aria-labelledby={`mpan-meter-label-${meter.id.toString()}`}>
                    <div className="input-group mb-1">
                      <span className="input-group-text">Top Line</span>
                      <input
                        type="text"
                        className="form-control"
                        value={ctx.mpanTopLine}
                        readOnly={readOnly}
                      />
                    </div>
                    <div className="input-group">
                      <span className="input-group-text">Bottom Line</span>
                      <input
                        type="text"
                        id={`mpan-meter-${meter.id.toString()}`}
                        className="form-control"
                        value={ctx.mpanBottomLine}
                        readOnly={readOnly}
                      />
                    </div>
                  </div>
                </div>
              )}
              {ctx.mprn && (
                <div className="sales-doc-field mt-2 read-only-field">
                  <label htmlFor={`mprn-meter-${meter.id.toString()}`}>MPRN</label>
                  <input
                    type="text"
                    id={`mprn-meter-${meter.id.toString()}`}
                    className="form-control"
                    value={ctx.mprn}
                    readOnly={readOnly}
                  />
                </div>
              )}
              {/* Hide supply address */}
              {ctx.supplyAddress.length > 0 && (
                <div className="sales-doc-field mt-2 read-only-field" hidden>
                  <span
                    id={`supply-address-meter-label-${meter.id.toString()}`}
                    className="sales-doc-group-label"
                  >
                    Supply Address
                  </span>
                  {ctx.supplyAddress.map((line, i) => (
                    <input
                      key={i}
                      id={`supply-address-meter-${meter.id.toString()}-${i.toString()}`}
                      aria-labelledby={`supply-address-meter-label-${meter.id.toString()}`}
                      type="text"
                      className="form-control mb-1"
                      value={line}
                      readOnly={readOnly}
                    />
                  ))}
                </div>
              )}
              <div
                className={`sales-doc-field mt-2${ctx.lockedFields.has("startDate") ? " read-only-field" : ""}`}
              >
                <div className="has-validation">
                  <label htmlFor={`start-date-meter-${meter.id.toString()}`}>Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    id={`start-date-meter-${meter.id.toString()}`}
                    value={ctx.startDate}
                    readOnly={ctx.lockedFields.has("startDate")}
                    onChange={(e) => {
                      ctx.setStartDate(e.target.value);
                    }}
                    required
                  />
                  <div className="invalid-feedback">Start date is required.</div>
                </div>
              </div>
              <div className="has-validation">
                <div
                  className={`sales-doc-field mt-2${ctx.lockedFields.has("endDate") ? " read-only-field" : ""}`}
                >
                  <label htmlFor={`end-date-meter-${meter.id.toString()}`}>End Date</label>
                  <input
                    type="date"
                    id={`end-date-meter-${meter.id.toString()}`}
                    className="form-control"
                    value={ctx.endDate}
                    readOnly={ctx.lockedFields.has("endDate")}
                    onChange={(e) => {
                      ctx.setEndDate(e.target.value);
                    }}
                    required
                  />
                  <div className="invalid-feedback">End date is required.</div>
                </div>
              </div>
            </Col>
            <Col>
              <div
                className={`sales-doc-field mt-2${ctx.lockedFields.has("contractType") ? " read-only-field" : ""}`}
              >
                <label htmlFor={`contract-type-meter-${meter.id.toString()}`}>Contract Type</label>
                <select
                  className="form-control"
                  id={`contract-type-meter-${meter.id.toString()}`}
                  value={ctx.contractType}
                  disabled={ctx.lockedFields.has("contractType")}
                  onChange={(e) => {
                    ctx.setContractType(e.target.value);
                  }}
                  required
                >
                  <option value="">Select contract type</option>
                  <option value="Bespoke">Bespoke</option>
                  <option value="Matrix">Matrix</option>
                </select>
              </div>
              {(ctx.passthrough || !ctx.lockedFields.has("passthrough")) && (
                <div
                  className={`sales-doc-field mt-2${ctx.lockedFields.has("passthrough") ? " read-only-field" : ""}`}
                >
                  <label htmlFor={`passthrough-meter-${meter.id.toString()}`}>Passthrough</label>
                  <select
                    id={`passthrough-meter-${meter.id.toString()}`}
                    className="form-control"
                    value={ctx.passthrough}
                    disabled={ctx.lockedFields.has("passthrough")}
                    onChange={(e) => {
                      ctx.setPassthrough(e.target.value);
                    }}
                  >
                    <option value="-None-">-None-</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Flexible">Flexible</option>
                    <option value="Full Pass-through">Full Pass-through</option>
                    <option value="Partial Pass-through">Partial Pass-through</option>
                    <option value="Multi Purchase">Multi Purchase</option>
                    <option value="Market Tracker">Market Tracker</option>
                    <option value="Market Choice">Market Choice</option>
                    <option value="Day Ahead">Day Ahead</option>
                    <option value="Simple Flex Enterprise">Simple Flex Enterprise</option>
                    <option value="Total Energies Flex">Total Energies Flex</option>
                  </select>
                </div>
              )}
              <div
                className={`sales-doc-field mt-2${ctx.lockedFields.has("commission") ? " read-only-field" : ""}`}
              >
                <label htmlFor={`commission-meter-${meter.id.toString()}`}>Commission</label>
                <div className="input-group mb-1">
                  <span className="input-group-text">£</span>
                  <input
                    type="number"
                    id={`commission-meter-${meter.id.toString()}`}
                    className="form-control"
                    value={ctx.commission}
                    onChange={(e) => {
                      ctx.setCommission(e.target.value);
                    }}
                    required
                    readOnly={ctx.lockedFields.has("commission")}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Collapse>
      <hr className="sales-doc-divider" />
    </>
  );
  return <>{apiFields}</>;
};
